import httpClient from './HttpClient'
import config from '@/assets/js/appsettings.json'
// Local dev: VUE_APP_API_URL (from .env.development.local, serve-only) overrides the baked API_URL.
// Unset in production builds → falls back to appsettings.json, so Azure bundles are unchanged.
const API_URL = process.env.VUE_APP_API_URL || config.API_URL

export default class FeapiService {
  constructor() {
  }

  ///----------------------------------------------------------------
  /// Search
  ///----------------------------------------------------------------

  search(data) {
    const url = `/part/search/?term=${data.term}&page=1&per_page=15`;
    return httpClient.get(url).then(response => response.data);
  }

  getPartSuggestion(data) {
    const params = new URLSearchParams();
    params.append('searchTerm', data.searchTerm);
    params.append('showWearPart', data.showWearPart);
    const url = `/Search/GetPartSuggestions/?${params.toString()}`;
    return httpClient.get(url).then((response) => response.data);
  }

  getAssembliesWithWearPartsOnSearch(data) {
    const params = new URLSearchParams();
    params.append('searchCode', data.searchCode);
    params.append('showWearPart', data.showWearPart);
    const url = `/Search/GetAssembliesWithWearParts/?${params.toString()}&showAllParts=${data.showAllParts}&companyId=${data.companyId}&machineId=${data.machineId}&assemblyId=${data.assemblyId}${data.searchDescription ? '&searchDescription=' + data.searchDescription : ''}`;
    return httpClient.get(url).then((response) => response.data);
  }

  resolveTopPartContext(data) {
    const params = new URLSearchParams();
    params.append('partId', data.partId);
    params.append('partCode', data.partCode);
    params.append('companyId', data.companyId);
    const url = `/order/resolve-top-part-context?${params.toString()}`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Assembly Details
  ///----------------------------------------------------------------

  getAssembliesList() {
    const url = `/assembly/GetSelectItems`;
    return httpClient.get(url).then(response => response.data);
  }

  getAssemblySelectItemsByAssembly(data) {
    const url = `/assembly/GetSelectItemsByAssembly/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  getAssemblyDetails(data) {
    const url = `/assembly/${data.id}/`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// SubAssembly Details
  ///----------------------------------------------------------------

  getAssemblyWithParts(data) {
    // Pass the active company so the backend hides assemblies/parts that have been
    // deactivated for this account (per-company). Defaults to the selected company.
    const companyId = (data && data.companyId) || localStorage.companyId || 0;
    const url = `/subassembly/GetAssemblyWithParts/${data.id}?companyId=${encodeURIComponent(companyId)}`;
    return httpClient.get(url).then(response => response.data);
  }

  getSubAssemblyWithParts(data) {
    const url = `/subassembly/GetSubAssemblyWithParts/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Content Block
  ///----------------------------------------------------------------

  getContentBlockDetails(block) {
    const url = `/contentblock/GetByName/${block}/`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Pages
  ///----------------------------------------------------------------

  getPagesDetails(page) {
    const url = `/page/GetByName/${page}/`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Part Details
  ///----------------------------------------------------------------

  getSelectItemsByAssembly(part) {
    const url = `/part/GetSelectItemsByAssembly/${part.id}/`;
    return httpClient.get(url).then(response => response.data);
  }

  getPartsByAssembly(part) {
    const url = `/part/GetItemsByAssembly/${part.id}/`;
    return httpClient.get(url).then(response => response.data);
  }

  // Resolves a part's Assembly + CompanyMachine context for the given
  // company. Used by the Frequently Ordered insights panel on
  // orders/create to construct saveCart payloads (PurchaseOrderModel
  // needs AssemblyId + CompanyMachineId — neither is derivable from
  // partId alone on the client). Backend method is
  // PartController.GetPartContext.
  getPartContext(data) {
    const params = new URLSearchParams();
    params.append('partId', data.partId);
    params.append('companyId', data.companyId);
    const url = `/part/PartContext?${params.toString()}`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Machine Details
  ///----------------------------------------------------------------

  getCompanyMachineDetails(data) {
    let url = `/companymachine/GetMachine/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  getMachinesList() {
    const url = `/machine/GetSelectItems`;
    return httpClient.get(url).then(response => response.data);
  }

  getSelectItemsByCompanyMachine(data) {
    const url = `/assembly/GetSelectItemsByCompanyMachine/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  getMachinesByCompany(data) {
    const url = `/CompanyMachine/GetSelectItems/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Wear Details
  ///----------------------------------------------------------------

  getWearItems(wear) {
    const url = `/wear/GetItemsByAssembly/${wear.id}/`;
    return httpClient.get(url).then(response => response.data);
  }

  // Returns a flat list of PartIds from dbo.Wears across all
  // companies — used by the Search / ShopWearParts / Cart /
  // RFQ pages to flag wear-eligible parts with a "2% Discount"
  // badge regardless of whether the current search is
  // wear-parts-only or all-parts. Wear-eligibility is intrinsic
  // to the part, not per-customer. The data argument is kept
  // for backward compatibility with existing callsites
  // (apiService.getWearPartIds({ companyId })) but ignored.
  // eslint-disable-next-line no-unused-vars
  getWearPartIds(data) {
    const url = `/wear/GetWearPartIds`;
    return httpClient.get(url).then(response => response.data);
  }

  // Company-scoped wear part ids (this company's dbo.Wears) — used for the
  // cart's "2% Discount" badge so it reflects the customer's Account Setup
  // wear list rather than the global wear-eligible catalog.
  getCompanyWearPartIds(companyId) {
    const url = `/wear/GetWearPartIdsByCompany/${encodeURIComponent(companyId)}`;
    return httpClient.get(url).then(response => response.data);
  }

  getAssembliesWithWearParts(wear) {
    const url = `/wear/GetAssembliesWithWearParts/${wear.assemblyId}/${wear.companyMachineId}`;
    return httpClient.get(url).then(response => response.data);
  }

  getAssembliesByMachine(wear) {
    const url = `/wear/GetAssemblyByMachineAndCompany/${wear.machineId}/${wear.companyId}`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Cart
  ///----------------------------------------------------------------

  saveCart(data) {
    const url = `/cart/`;
    return httpClient.post(url, data).then(response => response.data);
  }

  getCart(data) {
    const url = `/cart/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  // Surgical single-part quantity update for the floating CartWidget's
  // inline editor (green-check commit). Avoids the saveCart round-trip,
  // which would route an existing-cart edit through SaveOldCart and be
  // rejected by its "part already in cart" guard. Keyed by
  // { cartId, purchaseOrderId, partId, quantity }.
  updateCartPartQuantity(data) {
    const url = `/cart/updatePartQuantity`;
    return httpClient.post(url, data).then(response => response.data);
  }

  // Surgical single-part removal for the floating CartWidget's per-part ×
  // button. Like updateCartPartQuantity, this avoids the saveCart round-trip
  // (which routes an existing-cart edit through SaveOldCart, whose
  // DoesPartExistInTheCart guard rejects the REMAINING parts and silently
  // drops the removal). Keyed by { cartId, purchaseOrderId, partId }.
  removePart(data) {
    const url = `/cart/removePart`;
    return httpClient.post(url, data).then(response => response.data);
  }

  getActiveCarts(data) {
    let url = `/cart/active?_t=${Date.now()}`;
    if (data && data.companyId) {
      url += `&companyId=${data.companyId}`;
    }
    return httpClient.get(url).then(response => response.data);
  }

  getTeamActiveCarts(data) {
    let url = `/cart/team-active?_t=${Date.now()}`;
    if (data && data.companyId) {
      url += `&companyId=${data.companyId}`;
    }
    return httpClient.get(url).then(response => response.data);
  }

  deleteCart(data) {
    const url = `/cart/${data.id}`;
    return httpClient.delete(url).then(response => response.data);
  }

  // ===== SAVE PARTIAL QUOTES =====
  // Splits a subset of the active cart's POs off into a new
  // "saved for later" cart. Used by RFQSavedItems.vue's Save
  // for Later picker when the user selects only some quotes
  // (the all-selected case is handled client-side without an
  // API call).
  //
  // Body shape:
  //   {
  //     purchaseOrderIds: number[],   // PO ids to split off
  //     asQuote: boolean              // force new cart's
  //                                   // Discriminator=Rfq
  //                                   // regardless of source
  //   }
  //
  // The asQuote flag handles the case where the source cart
  // was created as Discriminator=Cart but the user is on the
  // Quote flow — without it, the new split cart would inherit
  // the wrong discriminator and appear under SavedOrders
  // instead of SavedQuotes.
  savePartialQuotes(data) {
    const url = `/cart/${data.cartId}/savepartial`;
    return httpClient.post(url, {
      purchaseOrderIds: data.purchaseOrderIds || [],
      asQuote: data.asQuote === undefined ? true : data.asQuote
    }).then(response => response.data);
  }

  // ===== CONVERT TO QUOTE =====
  // Inverse of convertFromQuote — flips the cart's TPH
  // discriminator from 'Cart' to 'Rfq' via raw SQL UPDATE
  // server-side. Idempotent (returns success if already
  // Rfq).
  //
  // Used by RFQSavedItems.vue's Save for Later flow to
  // ensure the cart is marked as a Quote on the server
  // before the user navigates away to /account/savedcarts.
  // Without this, an active cart with Discriminator=Cart
  // would persist as a SavedOrder despite the user
  // explicitly choosing to save it as a quote.
  convertToQuote(data) {
    const url = `/cart/converttoquote/${data.cartId}`;
    return httpClient.post(url).then(response => response.data);
  }

  bulkDeleteCarts(data) {
    return httpClient.post('/cart/bulkdelete', data).then(response => response.data);
  }

  /**
   * Mark a cart as submitted — graduates it from the
   * SavedCarts "Saved" tab to OrderHistory or RFQHistory
   * depending on its discriminator. Called by Cart Success
   * (after Place Order) and SavedRFQ (after Finalize / Print
   * Quote) once their existing archive lifecycles complete.
   *
   * Idempotent server-side, so a duplicate call is safe.
   */
  submitCart(data) {
    const url = `/cart/${data.id}/submit`;
    return httpClient.post(url, {}).then(response => response.data);
  }

  getDeletedCarts(data) {
    let url = `/cart/deleted?_t=${Date.now()}`;
    if (data && data.companyId) {
      url += `&companyId=${data.companyId}`;
    }
    // type: "saved" (unsubmitted drafts) | "quotes" (submitted RFQs).
    // Splits the deleted-cart list per SavedCarts tab; omit for all.
    if (data && data.type) {
      url += `&type=${data.type}`;
    }
    return httpClient.get(url).then(response => response.data);
  }

  restoreCarts(data) {
    return httpClient.post('/cart/restore', data).then(response => response.data);
  }

  // Bulk soft-delete (clear) submitted quotes across the FULL history.
  //   data.ids        → clear exactly those
  //   data.beforeDate → clear all scoped quotes on/before that date
  //   neither         → clear all scoped quotes
  clearQuotes(data) {
    var query = [];
    if (data && data.companyId) query.push('cid=' + data.companyId);
    if (data && data.accountWide) query.push('accountWide=true');
    var url = '/cart/clearquotes' + (query.length ? '?' + query.join('&') : '');
    var body = {};
    if (data && data.ids) body.ids = data.ids;
    if (data && data.beforeDate) body.beforeDate = data.beforeDate;
    return httpClient.post(url, body).then(response => response.data);
  }

  permanentDeleteCarts(data) {
    return httpClient.post('/cart/permanentdelete', data).then(response => response.data);
  }

  updatePONumbers(data) {
    const url = `/cart/UpdatePoNumbers/?cartId=${data.cartId}&poId=${data.poId}&newPoNumber=${data.newPoNumber}`;
    return httpClient.put(url, data).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Purchase Order
  ///----------------------------------------------------------------

  updatePurchaseOrder(data) {
    const url = `/purchaseorder/${data.id}`;
    return httpClient.put(url, data).then(response => response.data);
  }

  deletePoNumber(data) {
    const url = `/purchaseorder/${data.id}`;
    return httpClient.delete(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Company
  ///----------------------------------------------------------------

  getCompanyList(params) {
    // Optional params: { viewAsUserId } or { previewRole } — Master-only server-side; makes the
    // header company list mirror an active "view as" preview (the viewed identity's own list).
    const url = `/user/GetCompanySelectItems/`;
    return httpClient.get(url, { params: params || {} }).then(response => response.data);
  }

  getCompanyDetails(company) {
    const url = `/company/${company.id}/`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Create Order
  ///----------------------------------------------------------------

  createOrder(data) {
    const url = `/order/`;
    return httpClient.post(url, data).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Create RFQ
  ///----------------------------------------------------------------

  createRFQ(data) {
    const url = `/cart/`;
    return httpClient.post(url, data).then(response => response.data);
  }

  rfqOrder(data) {
    const url = `/cart/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  getQuotes(data) {
    let url = `/cart?page=${data.page}&per_page=${data.per_page}&sort=${data.sortBy}`;
    if (data.companyId) {
      url += `&cid=${data.companyId}`;
    }
    if (data.accountWide) {
      url += `&accountWide=true`;
    }
    return httpClient.get(url).then(response => response.data);
  }

  deleteQuote(data) {
    const url = `/cart/${data.id}`;
    return httpClient.delete(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Account Section
  ///----------------------------------------------------------------

  getOrders(data) {
    let url = `/order?page=${data.page}&per_page=${data.per_page}&sort=${data.sortBy}&filter=${data.filter}&poNumber=${data.poNumber}`;
    if (data.companyId) {
      url += `&companyId=${data.companyId}`;
    }
    if (data.accountWide) {
      url += `&accountWide=true`;
    }
    return httpClient.get(url).then(response => response.data);
  }

  getInsights(data) {
    let url = `/order/insights?_t=${Date.now()}`;
    if (data && data.companyId) {
      url += `&companyId=${data.companyId}`;
    }
    if (data && data.allTime) {
      url += `&allTime=true`;
    }
    if (data && data.accountWide) {
      url += `&accountWide=true`;
    }
    return httpClient.get(url).then(response => response.data);
  }

  reorder(data) {
    // companyId (optional) targets a CROSS-company reorder — clone the order
    // into a NEW cart under that company (Master only). Omitted = same-company
    // (cart under the order's own company). Backend: POST /cart/reorder/{id}.
    var url = `/cart/reorder/${data.id}`;
    if (data.companyId) {
      url += `?companyId=${data.companyId}`;
    }
    return httpClient.post(url, data).then(response => response.data);
  }

  // Read-only fetch of an ORDER's contents (string order id) rebuilt into
  // purchaseOrders, WITHOUT creating a cart. Used by the cross-company
  // "add to my company" re-order to load the order before re-saving it under
  // the current company. NOTE: hits the ORDER repository — do NOT use getCart()
  // (which is /cart/{id:int}, a CART id) with an order id: the string binds to
  // 0 and returns an opaque "Bad Request".
  reorderPreview(orderId) {
    const url = `/cart/reorder-preview/${orderId}`;
    return httpClient.get(url).then(response => response.data);
  }

  viewOrder(data) {
    const url = `/order/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  getUser(data) {
    const url = `/user/${data.id}`;
    return httpClient.get(url).then(response => response.data);
  }

  deleteOrder(data) {
    const url = `/order/${data.id}`;
    return httpClient.delete(url).then(response => response.data);
  }

  // Recently-deleted order history (soft-deleted via deleteOrder). Same
  // envelope as getOrders ({ data: [[...]], total }).
  getDeletedOrders(data) {
    let url = `/order/deleted?_t=${Date.now()}`;
    if (data && data.companyId) {
      url += `&companyId=${data.companyId}`;
    }
    if (data && data.accountWide) {
      url += `&accountWide=true`;
    }
    return httpClient.get(url).then(response => response.data);
  }

  // Restore soft-deleted orders. Body: { ids: string[] }.
  restoreOrders(data) {
    return httpClient.post('/order/restore', data).then(response => response.data);
  }

  // Bulk soft-delete (clear) orders across the FULL history. Modes:
  //   data.ids        → clear exactly those
  //   data.beforeDate → clear all scoped orders on/before that date
  //   neither         → clear all scoped orders
  // Scope: data.companyId (omit/empty = all companies for admins), data.accountWide.
  clearOrders(data) {
    var query = [];
    if (data && data.companyId) query.push('companyId=' + data.companyId);
    if (data && data.accountWide) query.push('accountWide=true');
    var url = '/order/clear' + (query.length ? '?' + query.join('&') : '');
    var body = {};
    if (data && data.ids) body.ids = data.ids;
    if (data && data.beforeDate) body.beforeDate = data.beforeDate;
    return httpClient.post(url, body).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Search Orders
  ///----------------------------------------------------------------

  searchOrders(data) {
    const url = `/order/?Status=${data.status}&UserId=${data.user}`;
    return httpClient.get(url).then(response => response.data);
  }

  searchRFQs(data) {
    const url = `/cart/?Status=${data.status}&UserId=${data.user}`;
    return httpClient.get(url).then(response => response.data);
  }

  getUsers() {
    const url = `/user/GetUserSelectItems`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Documents
  ///----------------------------------------------------------------

  getDocuments(search) {
    const url = `/document${search ? '?search=' + encodeURIComponent(search) : ''}`;
    return httpClient.get(url).then(response => response.data);
  }

  getMyDocuments(search, companyId) {
    const params = [];
    if (search) params.push('search=' + encodeURIComponent(search));
    if (companyId) params.push('companyId=' + encodeURIComponent(companyId));
    const url = `/document/my-machines${params.length ? '?' + params.join('&') : ''}`;
    return httpClient.get(url).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Account setup (Master-only audit)
  ///----------------------------------------------------------------

  getAccountSetup(companyId) {
    const q = companyId ? '?companyId=' + encodeURIComponent(companyId) : '';
    return httpClient.get(`/account/setup-summary${q}`).then(response => response.data);
  }

  getSetupCompanies() {
    return httpClient.get('/account/setup-companies').then(response => response.data);
  }

  copyAccountSetup(sourceCompanyId, targetCompanyId) {
    const q = `?sourceCompanyId=${encodeURIComponent(sourceCompanyId)}&targetCompanyId=${encodeURIComponent(targetCompanyId)}`;
    return httpClient.post(`/account/setup-copy${q}`).then(response => response.data);
  }

  undoAccountSetupCopy(payload) {
    return httpClient.post('/account/setup-copy-undo', payload).then(response => response.data);
  }

  redoAccountSetupCopy(payload) {
    return httpClient.post('/account/setup-copy-redo', payload).then(response => response.data);
  }

  getSetupAddable(companyId, type, term) {
    const q = `?companyId=${encodeURIComponent(companyId)}&type=${encodeURIComponent(type)}&term=${encodeURIComponent(term || '')}`;
    return httpClient.get(`/account/setup-addable${q}`).then(response => response.data);
  }

  addSetupLink(payload) {
    return httpClient.post('/account/setup-link-add', payload).then(response => response.data);
  }

  removeSetupLink(payload) {
    return httpClient.post('/account/setup-link-remove', payload).then(response => response.data);
  }

  getSetupGapDiagnosis(machineId) {
    return httpClient.get(`/account/setup-gap-diagnose?machineId=${encodeURIComponent(machineId)}`).then(response => response.data);
  }

  acknowledgeSetupGap(payload) {
    return httpClient.post('/account/setup-gap-acknowledge', payload).then(response => response.data);
  }

  getSetupEventLog() {
    return httpClient.get('/account/setup-event-log').then(response => response.data);
  }

  // Records a custom event into the same store getSetupEventLog reads, so it
  // shows up under Account Setup → Event Logs & History. Used by the
  // cross-company reorder FAILURE path to log the failure itself (current
  // company, order + its company, missing items + suggested fix, backend
  // error). Body maps to the backend SetupEventRequest:
  //   { kind, title, detail, companyId, company?, success?, exception?,
  //     suggestions?: string[], data?: { [k]: string } }
  recordSetupEvent(payload) {
    return httpClient.post('/account/setup-event', payload).then(response => response.data);
  }

  // ===== Assembly-diagram callout (balloon) overrides — admin map editor =====
  // GET returns the saved overrides blob ({} when none); POST (Master-only) saves
  // the whole blob. Backed by Settings JSON via CalloutController. The blob shape
  // ({ images: { <imagePath>: { w, h, balloons:[{code,x,y,w,h}], hidden:[code] } } })
  // is owned by Search/Index.vue's balloon editor.
  getCalloutOverrides() {
    return httpClient.get('/callout/overrides').then(response => response.data);
  }
  saveCalloutOverrides(payload) {
    return httpClient.post('/callout/overrides', payload).then(response => response.data);
  }

  // ----- Interactive guide tours (DB-backed via GuideTourController) -----
  // GET returns the full { flow: {steps:[...]} } object ({} when none); the Tour
  // Builder POSTs one flow (Master-only). Replaces the localStorage prototype so
  // a built tour persists across logins and is shared to every customer.
  getGuideTours() {
    return httpClient.get('/guidetour').then(response => response.data);
  }
  saveGuideTour(flow, payload) {
    return httpClient.post('/guidetour/' + encodeURIComponent(flow), payload).then(response => response.data);
  }
  // The stable, prune-proof audio URL for a tour step: serves the ACTIVE version's
  // clip for this step position, so a recycled/pruned step id can never 404. The
  // player builds THIS for playback instead of the stored /audio/{id} url.
  clipUrl(flow, index) {
    return API_URL + '/guidetour/' + encodeURIComponent(flow) + '/clip/' + index;
  }
  // Master-only: synthesize a preview clip of narration text in a neural voice.
  // Resolves to an audio Blob, or null when Azure Speech isn't configured (204)
  // so the caller falls back to the browser's built-in speech voice.
  // Master-only: the neural voices THIS Azure resource actually offers (so the
  // builder dropdown can't list voices that would synthesize silently). Returns
  // [{id, label}]; empty array if the endpoint/Azure isn't available.
  getTtsVoices() {
    return httpClient.get('/guidetour/tts/voices').then(r => r.data || []).catch(() => []);
  }
  // Master-only: every saved version of a flow (newest first) with steps + meta,
  // so the builder can review/restore an older version.
  getTourVersions(flow) {
    return httpClient.get('/guidetour/' + encodeURIComponent(flow) + '/versions').then(r => r.data || []).catch(() => []);
  }
  ttsPreview(text, voice) {
    return httpClient.post('/guidetour/tts/preview', { text: text, voice: voice }, { responseType: 'blob' })
      .then(r => {
        // Only an actual audio body counts — a JSON/empty/error response (e.g. an
        // older backend without this endpoint) must yield null so the caller
        // falls back to the browser voice instead of "playing" non-audio.
        const b = r && r.data;
        return (b && b.size && b.type && b.type.indexOf('audio') === 0) ? b : null;
      })
      .catch(() => null);
  }

  // ----- Recorded tour videos (admin "Preview tour" screen capture) -----
  // Master-only: upload a recorded WebM as THE video for a flow (overwrites the
  // prior one). `blob` is the MediaRecorder output. We send it as the raw request
  // body with its own content-type (overriding the client's JSON default).
  saveGuideVideo(flow, blob) {
    return httpClient.post('/guidetour/video/' + encodeURIComponent(flow), blob, {
      headers: { 'Content-Type': (blob && blob.type) || 'video/webm' }
    }).then(r => r.data);
  }
  // Anonymous: { exists, bytes } for a flow's recorded video, so the Guide can
  // prefer it over the static MP4 without downloading the whole blob.
  guideVideoInfo(flow) {
    return httpClient.get('/guidetour/video/' + encodeURIComponent(flow) + '/info')
      .then(r => (r && r.data) || { exists: false, bytes: 0 })
      .catch(() => ({ exists: false, bytes: 0 }));
  }
  // Absolute URL a <video> element can load directly (no token needed — the GET
  // is anonymous). Built off the same API base as every other call.
  getGuideVideoUrl(flow) {
    const base = (httpClient.defaults.baseURL || '').replace(/\/+$/, '');
    return base + '/guidetour/video/' + encodeURIComponent(flow);
  }
  // Master-only: remove a flow's recorded video so the Guide falls back to MP4.
  deleteGuideVideo(flow) {
    return httpClient.delete('/guidetour/video/' + encodeURIComponent(flow)).then(r => r.data);
  }

  // Master-only: set a single Part's catalog Item # (fills in a missing item code
  // from the Search page). Body: { item, code } — code is verified server-side.
  setPartItem(id, payload) {
    return httpClient.post('/part/' + id + '/item', payload).then(response => response.data);
  }

  diagnoseSetupEmail(email, type) {
    return httpClient.get(`/account/setup-email-diagnose?email=${encodeURIComponent(email || '')}&type=${encodeURIComponent(type || 'email-delivery')}`).then(response => response.data);
  }

  getSetupCopyHistory(companyId) {
    const q = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return httpClient.get(`/account/setup-copy-history${q}`).then(response => response.data);
  }

  getSetupPartSearch(companyId, term) {
    const q = `?companyId=${encodeURIComponent(companyId)}&term=${encodeURIComponent(term || '')}`;
    return httpClient.get(`/account/setup-part-search${q}`).then(response => response.data);
  }

  getSetupAssemblySearch(companyId, term, subOnly) {
    const q = `?companyId=${encodeURIComponent(companyId)}&term=${encodeURIComponent(term || '')}&subOnly=${subOnly ? 'true' : 'false'}`;
    return httpClient.get(`/account/setup-assembly-search${q}`).then(response => response.data);
  }

  setupAssemblyDeactivate(payload) {
    return httpClient.post('/account/setup-assembly-deactivate', payload).then(response => response.data);
  }

  requestUserPasswordHelp(payload) {
    return httpClient.post('/account/setup-user-password-request', payload).then(response => response.data);
  }

  createSetupUser(payload) {
    return httpClient.post('/account/setup-user-create', payload).then(response => response.data);
  }

  // Approve / un-approve an account WITHOUT opening the whole user form. Flips only
  // AspNetUsers.EmailConfirmed; Master-only and helpdesk-only for internal accounts (server-enforced).
  setUserApproved(payload) {
    return httpClient.post('/account/setup-user-approve', payload).then(response => response.data);
  }

  getViewableUsers() {
    return httpClient.get('/account/setup-viewable-users').then(response => response.data);
  }

  linkUserToCompany(payload) {
    return httpClient.post('/account/setup-user-link-company', payload).then(response => response.data);
  }

  unlinkUserFromCompany(payload) {
    return httpClient.post('/account/setup-user-unlink-company', payload).then(response => response.data);
  }

  getSetupUserCompanies(userId) {
    return httpClient.get(`/account/setup-user-companies?userId=${encodeURIComponent(userId || '')}`).then(response => response.data);
  }

  deactivateSetupUser(payload) {
    return httpClient.post('/account/setup-user-deactivate', payload).then(response => response.data);
  }

  deleteSetupUser(payload) {
    return httpClient.post('/account/setup-user-delete', payload).then(response => response.data);
  }

  searchSetupUsers(term) {
    return httpClient.get(`/account/setup-user-search?term=${encodeURIComponent(term || '')}`).then(response => response.data);
  }

  resendSetupLink(payload) {
    return httpClient.post('/account/setup-user-resend-setup-link', payload).then(response => response.data);
  }

  setupDeactivate(payload) {
    return httpClient.post('/account/setup-part-deactivate', payload).then(response => response.data);
  }

  setupWearDeleteAll(payload) {
    return httpClient.post('/account/setup-wear-delete-all', payload).then(response => response.data);
  }

  setupReactivate(logId) {
    return httpClient.post('/account/setup-part-reactivate', { logId: logId }).then(response => response.data);
  }

  // ===== Master-only edit / create of Account-Setup catalog objects =====
  // getSetupEntity(type,id) -> { code, label, fields:{...} } (uniform editable
  // fields, incl. Part.Item). setupEdit/setupCreate snapshot+log for one-click
  // revert via setupReactivate.
  getSetupEntity(type, id) {
    return httpClient.get(`/account/setup-entity?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}`).then(response => response.data);
  }
  setupEdit(payload) {
    return httpClient.post('/account/setup-edit', payload).then(response => response.data);
  }
  setupCreate(payload) {
    return httpClient.post('/account/setup-create', payload).then(response => response.data);
  }

  getSetupDeleteHistory(companyId, category) {
    const q = `?companyId=${encodeURIComponent(companyId)}&category=${encodeURIComponent(category)}`;
    return httpClient.get(`/account/setup-delete-history${q}`).then(response => response.data);
  }

  setupHistoryDelete(logId) {
    return httpClient.post('/account/setup-history-delete', { logId: logId }).then(response => response.data);
  }

  setupHistoryClear(companyId, category) {
    return httpClient.post('/account/setup-history-clear', { companyId: Number(companyId) || 0, category: category }).then(response => response.data);
  }

  getSetupPartImpact(partId) {
    return httpClient.get(`/account/setup-part-impact?partId=${encodeURIComponent(partId)}`).then(response => response.data);
  }

  getSetupPartReach(partIds) {
    return httpClient.post('/account/setup-part-reach', { partIds: partIds }).then(response => response.data);
  }

  getSetupGaps(companyId, kind) {
    const q = `?companyId=${encodeURIComponent(companyId)}&kind=${encodeURIComponent(kind || 'wear')}`;
    return httpClient.get(`/account/setup-gaps${q}`).then(response => response.data);
  }

  getSetupCopyDetail(logId) {
    return httpClient.get(`/account/setup-copy-detail?logId=${encodeURIComponent(logId)}`).then(response => response.data);
  }

  ///----------------------------------------------------------------
  /// Parts Inquiry
  ///----------------------------------------------------------------

  /**
   * Submit a parts inquiry from the "Don't see your machine?"
   * footer in the Create New Order / Create New Quote pages.
   *
   * Mirrors the bug-report flow in APIService.submitBug —
   * multipart/form-data with an optional file attachment.
   *
   *   data: FormData with fields:
   *     - CustomerName (required)
   *     - CustomerEmail (required)
   *     - CustomerCompany (optional)
   *     - MachineDescription (optional)
   *     - Description (required)
   *     - InquiryFile (optional File)
   *
   * Server posts the inquiry to parts@jcsteele.com using
   * the existing email pipeline; recipient is hardcoded
   * server-side so this endpoint can't be repurposed as a
   * generic email sender.
   */
  submitPartsInquiry(data) {
    const url = "/Email/parts-inquiry";
    return httpClient.post(url, data, {
      headers: { "Content-Type": "multipart/form-data" }
    }).then(response => response.data);
  }

  // Master: run sign-in provisioning for a user NOW (header "View as user" hook). Additive + idempotent.
  provisionUser(userId) {
    return httpClient.post("/Account/provision-user", { userId }).then(response => response.data);
  }

  /**
   * Master-only "Report incorrect chart" from the Search page's fullscreen
   * chart viewer. Plain JSON — the server re-validates the assembly, builds
   * the chart-page + upload links itself, and emails helpdesk@jcsteele.com
   * (recipient hardcoded server-side, same doctrine as parts-inquiry).
   */
  reportChart(data) {
    const url = "/Email/report-chart";
    return httpClient.post(url, data).then(response => response.data);
  }

  /**
   * Active portal users linked to the company with this customer CODE, for the
   * Sales-rep parts-request "known contacts" picker in the contact modal.
   * Returns { ok, users: [{ id, firstName, lastName, email, role }] }.
   */
  getCompanyContacts(code) {
    const url = `/user/company-contacts?code=${encodeURIComponent(code || "")}`;
    return httpClient.get(url).then(response => response.data);
  }

  /**
   * Browse User Insights — accounts ordered by most recent activity. Master-only
   * server-side, and only helpdesk@jcsteele.com sees internal @jcsteele.com accounts.
   * Returns [] rather than throwing when the table has not been deployed yet.
   */
  /** Account Status report — one pre-aggregated summary plus capped drill lists. */
  /** Generate Insights for an Event Logs report. The payload is rebuilt SERVER-side —
   *  we send only the report key, never the numbers, so the model cannot be fed forged data. */
  generateLogInsights(reportKey) {
    return httpClient.post("/activity/insights", { reportKey: reportKey }).then(r => r.data || {});
  }

  /** Chat for Event Logs & History. Only the report key + question travel — the backend
   *  grounds the answer in the same aggregate the cards render from. */
  /** Narrate one account's one day. The browser sends its UTC offset so "a day" means
   *  THEIR day — a late-evening session is already tomorrow in UTC. */
  summarizeUserDay(userId, date) {
    // The offset OF THAT DAY, not of today. Sending today's offset windowed any day on the
    // far side of a daylight-saving change an hour wrong, so a January day summarised in
    // August narrated 23:00 the previous evening through 23:00 — the first hour of the day
    // the heading showed was missing and an hour of the day before was folded in.
    var p = String(date || "").split("-");
    var local = p.length === 3 ? new Date(+p[0], +p[1] - 1, +p[2]) : new Date();
    return httpClient.post("/activity/day-summary", {
      userId: userId, date: date, tzOffsetMinutes: local.getTimezoneOffset()
    }).then(r => r.data || {});
  }

  askLogs(reportKey, question) {
    return httpClient.post("/activity/ask", { reportKey: reportKey, question: question }).then(r => r.data || {});
  }

  getAccountStatus() {
    return httpClient.get("/activity/account-status").then(r => r.data || null);
  }

  getActivityUsers(days) {
    const url = `/activity/users?days=${encodeURIComponent(days || 30)}`;
    return httpClient.get(url).then(r => (Array.isArray(r.data) ? r.data : []));
  }

  /**
   * One account's event timeline, newest first. Keyset-paged on `before` because a
   * click-level trail runs to tens of thousands of rows per user.
   */
  /** `beforeId` is half the cursor: OccurredAtUtc alone is not unique, so paging on the
   *  timestamp by itself skipped rows recorded in the same millisecond. */
  getActivityTimeline(userId, before, take, beforeId) {
    let url = `/activity/timeline?userId=${encodeURIComponent(userId || "")}&take=${encodeURIComponent(take || 200)}`;
    if (before) url += `&before=${encodeURIComponent(before)}`;
    if (beforeId) url += `&beforeId=${encodeURIComponent(beforeId)}`;
    return httpClient.get(url).then(r => (Array.isArray(r.data) ? r.data : []));
  }
}